import { useEffect, useState } from "react";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { useAuth } from "../../context/AuthContext";
import { api, errorMessage } from "../../lib/api";
import { toast } from "sonner";

export default function Wallet() {
    const { user, refresh } = useAuth();
    const [items, setItems] = useState([]);
    const [points, setPoints] = useState(1000);
    const [method, setMethod] = useState("paypal");
    const [paypalEmail, setPaypalEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/cashout/me").then((r) => setItems(r.data || [])).catch(() => {});
    }, []);

    const usd = (points / 100).toFixed(2);
    const lifetime = user?.lifetime_points || 0;
    const w9Required = lifetime >= 60000 && !user?.w9_filed;
    const w9Approaching = !w9Required && lifetime >= 50000 && !user?.w9_filed;
    const emailVerified = !!user?.email_verified;
    const canCashout =
        points >= 1000 &&
        points <= (user?.points || 0) &&
        !w9Required &&
        emailVerified;

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const details = method === "paypal" ? { email: paypalEmail } : {};
            await api.post("/cashout", { points, method, details });
            toast.success(`Cashout requested: $${usd}`);
            const fresh = await api.get("/cashout/me");
            setItems(fresh.data || []);
            await refresh();
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-paper min-h-screen">
            <Navbar />
            <main className="max-w-5xl mx-auto px-6 py-12">
                <p className="eyebrow mb-2">WALLET</p>
                <h1 className="h2 mb-8" data-testid="wallet-title">Cash out your points.</h1>

                {!emailVerified && (
                    <div className="mb-8 px-5 py-4 bg-info/10 border border-info/40 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="wallet-email-verify-banner">
                        <div>
                            <p className="font-semibold text-sm">Verify your email before cashing out.</p>
                            <p className="text-xs text-ink-600 mt-1">
                                We sent a link to <span className="font-mono">{user?.email}</span>. Click it, or resend below.
                            </p>
                        </div>
                        <button
                            onClick={async () => {
                                try {
                                    await api.post("/auth/send-verification");
                                    toast.success("Verification email sent");
                                } catch (e) {
                                    toast.error(errorMessage(e));
                                }
                            }}
                            className="btn btn-ink text-xs py-2 px-3"
                            data-testid="wallet-resend-verify-btn"
                        >
                            Resend verification
                        </button>
                    </div>
                )}

                {(w9Required || w9Approaching) && (
                    <W9Block
                        required={w9Required}
                        lifetime={lifetime}
                        onFiled={async () => { await refresh(); }}
                    />
                )}
                <div className="grid lg:grid-cols-2 gap-6 mb-12">
                    <div className="card-base p-6">
                        <div className="eyebrow mb-1">CURRENT BALANCE</div>
                        <div className="font-mono text-5xl font-semibold tracking-tight" data-testid="wallet-balance">
                            {(user?.points || 0).toLocaleString()} <span className="text-respondent text-3xl">pts</span>
                        </div>
                        <div className="text-sm text-ink-600 mt-2">≈ ${((user?.points || 0) / 100).toFixed(2)} USD · 100 pts = $1</div>
                        <div className="mt-6 text-xs text-ink-600">Minimum cashout: $10.00 (1,000 pts). Processed within 48h.</div>
                    </div>
                    <form onSubmit={submit} className="card-base p-6 space-y-4">
                        <p className="eyebrow">REQUEST CASHOUT</p>
                        <div>
                            <label className="text-xs block mb-1">Points to cash out</label>
                            <input type="number" min={1000} step={100} value={points} onChange={(e) => setPoints(Number(e.target.value))} className="input-base" data-testid="cashout-points-input" />
                            <div className="text-xs font-mono mt-1">= ${usd} USD</div>
                        </div>
                        <div>
                            <label className="text-xs block mb-1">Method</label>
                            <select value={method} onChange={(e) => setMethod(e.target.value)} className="input-base" data-testid="cashout-method-select">
                                <option value="paypal">PayPal</option>
                                <option value="stripe">Stripe (bank)</option>
                                <option value="gift_card">Gift card</option>
                            </select>
                        </div>
                        {method === "paypal" && (
                            <div>
                                <label className="text-xs block mb-1">PayPal email</label>
                                <input type="email" value={paypalEmail} onChange={(e) => setPaypalEmail(e.target.value)} className="input-base" required data-testid="cashout-paypal-input" />
                            </div>
                        )}
                        <button type="submit" disabled={!canCashout || submitting} className="btn btn-respondent w-full" data-testid="cashout-submit-btn">
                            {submitting ? "Submitting…" : `Request $${usd}`}
                        </button>
                        {!canCashout && (
                            <p className="text-xs text-ink-600">
                                {(user?.points || 0) < 1000
                                    ? `You need ${1000 - (user?.points || 0)} more points to cash out.`
                                    : "Cannot exceed your balance."}
                            </p>
                        )}
                    </form>
                </div>

                <p className="eyebrow mb-3">HISTORY</p>
                <div className="card-base">
                    {items.length === 0 && <div className="p-8 text-sm text-ink-600 text-center">No cashouts yet.</div>}
                    {items.map((c) => (
                        <div key={c.id} className="flex items-center justify-between p-4 border-b border-ink-200 last:border-b-0" data-testid={`cashout-row-${c.id}`}>
                            <div>
                                <div className="font-mono">${c.usd.toFixed(2)} via {c.method}</div>
                                <div className="text-xs text-ink-600">{new Date(c.created_at).toLocaleString()}</div>
                            </div>
                            <span className={`font-mono text-[10px] px-2 py-1 ${c.status === "paid" ? "bg-success/15 text-success" : c.status === "rejected" ? "bg-error/15 text-error" : "bg-warning/15 text-warning"}`}>
                                {c.status.toUpperCase()}
                            </span>
                        </div>
                    ))}
                </div>
            </main>
            <Footer />
        </div>
    );
}



function W9Block({ required, lifetime, onFiled }) {
    const [open, setOpen] = useState(required);
    const [form, setForm] = useState({ full_name: "", address: "", tax_id_last4: "", signature: "" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (form.tax_id_last4.length !== 4 || !/^\d{4}$/.test(form.tax_id_last4)) {
            toast.error("Last 4 of tax ID must be 4 digits");
            return;
        }
        if (!form.signature.trim()) {
            toast.error("Type your full name as electronic signature");
            return;
        }
        setBusy(true);
        try {
            await api.post("/users/me/w9", form);
            toast.success("W-9 on file. You can now cash out.");
            await onFiled();
            setOpen(false);
        } catch (_e) {
            toast.error("Failed to submit W-9");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={`mb-8 px-5 py-4 ${required ? "bg-error/10 border border-error/40" : "bg-warning/10 border border-warning/40"}`} data-testid={required ? "w9-required-banner" : "w9-approaching-banner"}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <p className="font-semibold text-sm">
                        {required ? "IRS Form W-9 required to continue cashing out" : "You're approaching the $600 lifetime threshold"}
                    </p>
                    <p className="text-xs text-ink-600 mt-1">
                        Lifetime earnings: <span className="font-mono">${(lifetime / 100).toFixed(2)}</span> · US tax law requires a W-9 once you hit $600.
                    </p>
                </div>
                <button onClick={() => setOpen((v) => !v)} className="btn btn-ink text-xs py-2 px-3" data-testid="w9-toggle-btn">
                    {open ? "Hide form" : "File W-9"}
                </button>
            </div>
            {open && (
                <form onSubmit={submit} className="mt-5 grid sm:grid-cols-2 gap-3 bg-white p-4 border border-ink-200" data-testid="w9-form">
                    <input className="input-base text-sm" placeholder="Full legal name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="w9-name" />
                    <input className="input-base text-sm" placeholder="Last 4 of SSN / EIN" required maxLength={4} value={form.tax_id_last4} onChange={(e) => setForm({ ...form, tax_id_last4: e.target.value.replace(/\D/g, "") })} data-testid="w9-tax-id" />
                    <input className="input-base text-sm sm:col-span-2" placeholder="Mailing address (street, city, state, zip)" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="w9-address" />
                    <input className="input-base text-sm sm:col-span-2" placeholder="Type full legal name as electronic signature" required value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} data-testid="w9-signature" />
                    <p className="text-[10px] text-ink-600 sm:col-span-2 font-mono">
                        Under penalties of perjury, I certify the information above is true. SocietyIntent will issue a 1099-NEC at year-end if cumulative payments exceed $600.
                    </p>
                    <button type="submit" disabled={busy} className="btn btn-ink sm:col-span-2 text-sm" data-testid="w9-submit-btn">
                        {busy ? "Submitting…" : "File W-9"}
                    </button>
                </form>
            )}
        </div>
    );
}
